import { RelativeDateDisplayMetric } from '@pushly/models/lib/enums/relative-date-display-metric'
import { NotificationDto } from '../../notifications'
import { NotificationPayload } from './notification-payload'
import { Edge, Node } from '@xyflow/react'
import { UIEditTrackable } from '../../../structs/ui-edit-trackable'
import { JourneyActionType } from '@pushly/models/lib/enums/journeys/journey-action-type'
import { BlueConicRecommendedNotificationParams } from '@pushly/models/lib/structs/journeys'
import { DelayType, GlobalTriggerType, StepType } from '@pushly/models/lib/enums/journeys'
import { TriggerType } from '@pushly/models/lib/enums'
import {
    CartedCatalogItemPickStrategy,
    SavedCatalogItemPickStrategy,
    ViewedCatalogItemPickStrategy,
} from '@pushly/models/lib/enums/catalogs'
import { ExitType } from '@pushly/models/lib/enums/journeys/exit-type.enum'

// region Journey Nodes / Steps
// region Param Types

export type DelayParams = {
    delay_seconds: number
    qualifier: RelativeDateDisplayMetric
}

export type SendActionParams = {
    notification?: NotificationDto | NotificationPayload
    notification_id?: number | string
    title?: string
}

export type SendCartActionParams = SendActionParams & {
    item_strategy?: CartedCatalogItemPickStrategy
}

export type SendViewedItemActionParams = SendActionParams & {
    item_strategy?: ViewedCatalogItemPickStrategy
}

export type SendSavedItemActionParams = SendActionParams & {
    item_strategy?: SavedCatalogItemPickStrategy
}

export type JourneyExitParams = {
    exit_state: 'completed'
}

// endregion

// region Configuration types

export type BaseJourneyConfig<
    CType extends
        | JourneyActionType
        | TriggerType
        | GlobalTriggerType
        | DelayType.RELATIVE
        | ExitType
        | undefined = undefined,
> = {
    out_step_id: number | string
    type?: CType
}

export type DelayConfiguration = BaseJourneyConfig<DelayType.RELATIVE> & {
    params: DelayParams
}

export type SendNotificationConfiguration = BaseJourneyConfig<JourneyActionType.SEND_NOTIFICATION> & {
    params: SendActionParams
}

export type SendCartNotificationConfiguration = BaseJourneyConfig<JourneyActionType.SEND_CART_NOTIFICATION> & {
    params: SendCartActionParams
}

export type SendRecommendedNotificationConfiguration =
    BaseJourneyConfig<JourneyActionType.SEND_RECOMMENDED_NOTIFICATION> & {
        params: BlueConicRecommendedNotificationParams
    }

export type SendViewedItemNotificationConfiguration =
    BaseJourneyConfig<JourneyActionType.SEND_VIEWED_ITEM_NOTIFICATION> & {
        params: SendViewedItemActionParams
    }

export type SendSavedItemNotificationConfiguration =
    BaseJourneyConfig<JourneyActionType.SEND_SAVED_ITEM_NOTIFICATION> & {
        params: SendSavedItemActionParams
    }

export type ActionConfiguration =
    | SendNotificationConfiguration
    | SendCartNotificationConfiguration
    | SendRecommendedNotificationConfiguration
    | SendViewedItemNotificationConfiguration
    | SendSavedItemNotificationConfiguration

export type GlobalTriggerConfiguration = BaseJourneyConfig<GlobalTriggerType> & {}

export type TriggerConfiguration = BaseJourneyConfig<TriggerType> & {}

export type ExitConfiguration = BaseJourneyConfig & {
    params: JourneyExitParams
}

// endregion

// region JourneyStep types

export type BaseJourneyStep<T extends StepType = any> = {
    id: string | number
    revision_id?: number
    campaign_id?: number
    type: T
    meta: Record<string, any>
}

export type DelayStep = BaseJourneyStep<StepType.DELAY> & {
    configuration: DelayConfiguration
}

export type ActionStep = BaseJourneyStep<StepType.ACTION> & {
    configuration: ActionConfiguration
}

export type SendNotificationActionStep = ActionStep & { configuration: SendNotificationConfiguration }

export function isSendNotificationActionStep(step: ActionStep): step is SendNotificationActionStep {
    return step.configuration.type === JourneyActionType.SEND_NOTIFICATION
}

export function isSendNotificationNode(node: JourneyNode): node is JourneyNode<SendNotificationActionStep> {
    return isActionJourneyNode(node) && node.data.step.configuration.type === JourneyActionType.SEND_NOTIFICATION
}

export type SendCartNotificationActionStep = ActionStep & { configuration: SendCartNotificationConfiguration }

export function isSendCartNotificationActionStep(step: ActionStep): step is SendCartNotificationActionStep {
    return step.configuration.type === JourneyActionType.SEND_CART_NOTIFICATION
}

export function isSendCartNotificationNode(node: JourneyNode): node is JourneyNode<SendCartNotificationActionStep> {
    return isActionJourneyNode(node) && node.data.step.configuration.type === JourneyActionType.SEND_CART_NOTIFICATION
}

// region SendViewed

export type SendViewedItemNotificationActionStep = ActionStep & {
    configuration: SendViewedItemNotificationConfiguration
}

export function isSendViewedItemNotificationActionStep(step: ActionStep): step is SendViewedItemNotificationActionStep {
    return step.configuration.type === JourneyActionType.SEND_VIEWED_ITEM_NOTIFICATION
}

export function isSendViewedItemNotificationNode(
    node: JourneyNode,
): node is JourneyNode<SendViewedItemNotificationActionStep> {
    return (
        isActionJourneyNode(node) &&
        node.data.step.configuration.type === JourneyActionType.SEND_VIEWED_ITEM_NOTIFICATION
    )
}

// endregion

// region SendSaved

export type SendSavedItemNotificationActionStep = ActionStep & { configuration: SendSavedItemNotificationConfiguration }

export function isSendSavedItemNotificationActionStep(step: ActionStep): step is SendSavedItemNotificationActionStep {
    return step.configuration.type === JourneyActionType.SEND_SAVED_ITEM_NOTIFICATION
}

export function isSendSavedItemNotificationNode(
    node: JourneyNode,
): node is JourneyNode<SendSavedItemNotificationActionStep> {
    return (
        isActionJourneyNode(node) &&
        node.data.step.configuration.type === JourneyActionType.SEND_SAVED_ITEM_NOTIFICATION
    )
}

// endregion

// region SendRecommended
export type SendRecommendedNotificationActionStep = ActionStep & {
    configuration: SendRecommendedNotificationConfiguration
}

export function isSendRecommendedNotificationActionStep(
    step: ActionStep,
): step is SendRecommendedNotificationActionStep {
    return step.configuration.type === JourneyActionType.SEND_RECOMMENDED_NOTIFICATION
}

export function isSendRecommendedNotificationNode(
    node: JourneyNode,
): node is JourneyNode<SendRecommendedNotificationActionStep> {
    return (
        isActionJourneyNode(node) &&
        node.data.step.configuration.type === JourneyActionType.SEND_RECOMMENDED_NOTIFICATION
    )
}

// endregion

export type GlobalTriggerStep = BaseJourneyStep<StepType.GLOBAL_TRIGGER> & {
    configuration: any
}

export type TriggerStep = BaseJourneyStep<StepType.TRIGGER> & {
    configuration: TriggerConfiguration
}

export type ExitStep = BaseJourneyStep<StepType.EXIT> & {
    configuration: ExitConfiguration
}

export type JourneyStep = GlobalTriggerStep | TriggerStep | DelayStep | ActionStep | ExitStep

// 1) Union for all actions whose params extend SendActionParams
export type SendWithParamsActionStep =
    | SendNotificationActionStep
    | SendCartNotificationActionStep
    | SendViewedItemNotificationActionStep
    | SendSavedItemNotificationActionStep

export function isSendWithParamsActionStep(step: ActionStep): step is SendWithParamsActionStep {
    switch (step.configuration.type) {
        case JourneyActionType.SEND_NOTIFICATION:
        case JourneyActionType.SEND_CART_NOTIFICATION:
        case JourneyActionType.SEND_VIEWED_ITEM_NOTIFICATION:
        case JourneyActionType.SEND_SAVED_ITEM_NOTIFICATION:
        case JourneyActionType.SEND_RECOMMENDED_NOTIFICATION:
            return true
        default:
            return false
    }
}

// 2) Subset that also supports item_strategy
export type SendWithItemStrategyActionStep =
    | SendCartNotificationActionStep
    | SendViewedItemNotificationActionStep
    | SendSavedItemNotificationActionStep

export function isSendWithItemStrategyActionStep(step: ActionStep): step is SendWithItemStrategyActionStep {
    switch (step.configuration.type) {
        case JourneyActionType.SEND_CART_NOTIFICATION:
        case JourneyActionType.SEND_VIEWED_ITEM_NOTIFICATION:
        case JourneyActionType.SEND_SAVED_ITEM_NOTIFICATION:
            return true
        default:
            return false
    }
}

export function getSendParams(step: ActionStep): SendActionParams | undefined {
    return isSendWithParamsActionStep(step) ? step.configuration.params : undefined
}

export function getItemStrategy(step: ActionStep) {
    return isSendWithItemStrategyActionStep(step) ? step.configuration.params.item_strategy : undefined
}

// endregion

// region JourneyNode / Edge Types
export type JourneyNodeDataType<T> = { step: T; uiMeta: Record<string, any> }
export type JourneyNode<T extends JourneyStep = JourneyStep> = Node<JourneyStepData<JourneyNodeDataType<T>>>

export function isGlobalTriggerJourneyNode(node: JourneyNode): node is JourneyNode<GlobalTriggerStep> {
    return node.data.step.type === StepType.GLOBAL_TRIGGER
}

export function isTriggerJourneyNode(node: JourneyNode): node is JourneyNode<TriggerStep> {
    return node.data.step.type === StepType.TRIGGER
}

export function isDelayJourneyNode(node: JourneyNode): node is JourneyNode<DelayStep> {
    return node.data.step.type === StepType.DELAY
}

export function isActionJourneyNode(node: JourneyNode): node is JourneyNode<ActionStep> {
    return node.data.step.type === StepType.ACTION
}

export function isExitJourneyNode(node: JourneyNode): node is JourneyNode<ExitStep> {
    return node.data.step.type === StepType.EXIT
}

// @todo NS-1663 - determine if Edges require data? May need for branching / etc features?
export type JourneyEdge = Edge<any>

// endregion
// endregion

// region UIEditTrackingClass
export class JourneyStepData<DataType extends JourneyNodeDataType<JourneyStep>> extends UIEditTrackable<DataType> {
    readonly [x: string]: unknown

    constructor(data: DataType) {
        super(data, data.step.id)
    }

    public get step(): DataType['step'] {
        return this.getData().step
    }

    public setStep(value: DataType['step']) {
        this.setData({ ...this.getData(), step: value })
    }

    public get uiMeta(): DataType['uiMeta'] {
        return this.getData().uiMeta
    }

    public setUIMeta(value: DataType['uiMeta']) {
        this.setData({ ...this.getData(), uiMeta: value })
    }
}

// endregion

// region Util Types

// available types of nodes to add
export type AddStepType =
    | StepType.DELAY
    | JourneyActionType.SEND_NOTIFICATION
    | JourneyActionType.SEND_CART_NOTIFICATION
    | JourneyActionType.SEND_VIEWED_ITEM_NOTIFICATION
    | JourneyActionType.SEND_SAVED_ITEM_NOTIFICATION
    | JourneyActionType.SEND_RECOMMENDED_NOTIFICATION

export type EditableNode = JourneyNode<
    SendNotificationActionStep | SendCartNotificationActionStep | SendRecommendedNotificationActionStep | DelayStep
>

export type NodeEditorRef =
    | {
          onSubmit: () => JourneyNode<DelayStep> | Promise<JourneyNode<ActionStep> | void>
      }
    | undefined

// endregion
